// authErrors — map raw Firebase Auth error codes to a structured, user-facing
// alert: { title, detail?, action? }.
//
// Firebase throws errors whose `.message` is a developer string like
// "Firebase: Error (auth/email-already-in-use)." Surfacing that verbatim is
// meaningless to a member (and off-brand). This translates the stable
// `error.code` into a headline + body + optional recovery action that the
// AuthPanel renders in its alert banner. Falls back to a generic message for
// anything unmapped, and NEVER leaks the raw Firebase string.
//
//   FirebaseError -> [authError] -> { title, detail, action }

const DEFAULT_TITLE = "Unable to proceed";
const DEFAULT_DETAIL = "Something went wrong. Try again in a moment.";

// Action.type is interpreted by AuthPanel: 'recover' triggers the password-reset
// flow with the entered email; 'login' switches to the Log In tab.
const RECOVER = { type: "recover", label: "Recover account" };

// The credential-mismatch case is deliberately vague (email vs password not
// distinguished) — matches Firebase's own invalid-credential collapse and avoids
// leaking which field was wrong.
const CREDENTIAL_MISMATCH = {
  title: "Email and password do not match",
  detail:
    "If you're sure the information you entered is correct, try recovering your account.",
  action: RECOVER,
};

// Keyed on Firebase's `error.code`. Titles are sentence-case (the alert headline);
// detail is the supporting line. Rejection copy stays editorial, not caps chrome.
const MESSAGES = {
  "auth/invalid-credential": CREDENTIAL_MISMATCH,
  "auth/wrong-password": CREDENTIAL_MISMATCH,
  "auth/user-not-found": CREDENTIAL_MISMATCH,
  "auth/email-already-in-use": {
    title: "That email is already registered",
    detail: "Log in instead, or recover your account if you've forgotten the password.",
    action: { type: "login", label: "Log in instead" },
  },
  "auth/invalid-email": {
    title: "That email address isn't valid",
    detail: "Check the address and try again.",
  },
  "auth/missing-password": {
    title: "Enter your password",
    detail: "A password is required to continue.",
  },
  "auth/weak-password": {
    title: "Choose a stronger password",
    detail: "Use at least six characters.",
  },
  "auth/too-many-requests": {
    title: "Too many attempts",
    detail: "Wait a moment, then try again.",
  },
  "auth/network-request-failed": {
    title: "Network error",
    detail: "Check your connection and try again.",
  },
  "auth/user-disabled": {
    title: "This account has been disabled",
    detail: "Contact support if you think this is a mistake.",
  },
  "auth/popup-closed-by-user": { title: "Sign-in was cancelled" },
  "auth/cancelled-popup-request": { title: "Sign-in was cancelled" },
  "auth/popup-blocked": {
    title: "Sign-in window blocked",
    detail: "Allow pop-ups for this site and try again.",
  },
  "auth/account-exists-with-different-credential": {
    title: "Email already registered",
    detail: "That email is registered with a different sign-in method.",
  },
};

// authError returns shared references from MESSAGES, so freeze them (and their
// nested action) — a caller that mutated a returned alert would otherwise corrupt
// the constant for every later error.
Object.values(MESSAGES).forEach((m) => {
  if (m.action) Object.freeze(m.action);
  Object.freeze(m);
});

// authError(error, fallbackDetail?) -> { title, detail?, action? }
// Prefers a mapped alert for the error's code, else a generic alert whose detail
// is the caller's fallback. Never returns the raw Firebase string.
export function authError(error, fallbackDetail) {
  const code = error && typeof error.code === "string" ? error.code : "";
  if (code && MESSAGES[code]) return MESSAGES[code];
  return { title: DEFAULT_TITLE, detail: fallbackDetail || DEFAULT_DETAIL };
}

export const _DEFAULT_TITLE = DEFAULT_TITLE;
export const _DEFAULT_DETAIL = DEFAULT_DETAIL;
